import React from 'react'
import { UserService } from '@duck/api-definitions'
import { SinglePick, Switch } from '@duck/ui'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { useTar } from '@/modules'

const FEEDBACK_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'he', label: 'Hebrew' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
]

interface SettingsSectionProps {
  settingsData: Partial<UserService.UserSettings>
  onChange: (field: keyof UserService.UserSettings, value: unknown) => void
}

export const SettingsSection: React.FC<SettingsSectionProps> = ({
  settingsData,
  onChange,
}) => {
  const { t } = useTar()

  return (
    <div className="space-y-6">
      {/* Boolean settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('users.userPreferences')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <label className="flex items-center gap-2">
            <Switch
              checked={!!settingsData.dismissedPersonalInfo}
              onCheckedChange={(checked) => onChange('dismissedPersonalInfo', checked === true)}
            />
            <span className="text-sm text-foreground">{t('users.dismissedPersonalInfo')}</span>
          </label>
          <label className="flex items-center gap-2">
            <Switch
              checked={!!settingsData.allowsAnonymousExposure}
              onCheckedChange={(checked) => onChange('allowsAnonymousExposure', checked === true)}
            />
            <span className="text-sm text-foreground">{t('users.allowsAnonymousExposure')}</span>
          </label>
          <label className="flex items-center gap-2">
            <Switch
              checked={!!settingsData.dismissedCandidateBetaOffer}
              onCheckedChange={(checked) => onChange('dismissedCandidateBetaOffer', checked === true)}
            />
            <span className="text-sm text-foreground">{t('users.dismissedCandidateBetaOffer')}</span>
          </label>
          <label className="flex items-center gap-2">
            <Switch
              checked={!!settingsData.dismissedRodeoWorkspaceVideo}
              onCheckedChange={(checked) => onChange('dismissedRodeoWorkspaceVideo', checked === true)}
            />
            <span className="text-sm text-foreground">{t('users.dismissedRodeoWorkspaceVideo')}</span>
          </label>
        </div>
      </div>

      {/* Language settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('users.languageSettings')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">{t('users.feedbackLanguage')}</label>
            <SinglePick
              options={FEEDBACK_LANGUAGE_OPTIONS}
              selected={settingsData.feedbackLanguage || ''}
              onChange={(value) => onChange('feedbackLanguage', value || undefined)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Read-only settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-foreground border-b border-border pb-2">
          {t('users.systemSettings')}
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
          <div className="space-y-1">
            <label className="block font-medium text-muted-foreground">{t('users.releaseNotesLastReadDate')}</label>
            <p className="text-foreground">
              {settingsData.releaseNotesLastReadDate
                ? <RelativeDateTime isoDateString={settingsData.releaseNotesLastReadDate} />
                : '-'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
